/**
 * The colours of a collection's art: the AI cover's prompt
 * (`domain/cover-art.ts`) and the programmatic backdrop the webapp draws for a
 * collection with no image. One palette, so both read as one family. These are
 * cover-art anchors, NOT the UI design-system tokens (`apps/webapp/src/App.css`).
 */

type AnchorColor = { name: string; hex: string };

/**
 * Curated anchor tones for the abstract grammar — the dominant colour a cover is
 * built on. Deep/mid tones only: each has to hold as the ground under a luminous
 * warm accent, so anything bright enough to compete with the accent is excluded.
 *
 * DELIBERATELY ABSENT as anchors, do not add them: Juisci primary yellow
 * (#FFC408), JuisciGPT lime (#EEF822) and pure lime (#2ECCAA). They are
 * warm-accent / UI colours; as an anchor they wash the whole frame out and
 * collide with WARM_LIGHT.
 */
export const ANCHOR_PALETTE = [
  { name: "deep teal slate", hex: "#14494E" },
  { name: "slate blue-grey", hex: "#3A4C5A" },
  { name: "deep forest green", hex: "#1F3B32" },
  { name: "deep charcoal ink", hex: "#212121" },
  { name: "deep bluegrey", hex: "#313B42" },
  { name: "soft sage green", hex: "#6E7F6A" },
  { name: "burgundy wine", hex: "#5A2233" },
  { name: "warm umber brown", hex: "#4A3527" },
  { name: "muted olive", hex: "#4E5233" },
  { name: "terracotta rust", hex: "#8C4A2F" },
  { name: "dusty rose mauve", hex: "#8C6A72" },
  { name: "raspberry", hex: "#CE0868" },
] as const satisfies ReadonlyArray<AnchorColor>;

/**
 * The luminous accent, identical on every cover and in every room. One shared
 * accent is what makes the set read as one family; varying it per room was
 * considered and rejected for v1 (see docs/playlist-default-image-generation.md
 * — "client playlists anchored on their room's brand colour" is a known
 * follow-up, and would move the ANCHOR, not this).
 */
export const WARM_LIGHT = { name: "warm cream-gold", hex: "#FBE7C0" } as const;
